import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ControlBadge } from "@/components/ControlBadge";
import {
  Loader2, Building2, Plus, MapPin, Pencil, Globe, ExternalLink, Lock, Archive,
} from "lucide-react";
import { HelpButton } from "@/components/help/HelpButton";

const NAVY = "#1C4358";

const STATUS_STYLE = {
  published: { bg: "rgba(47,125,90,0.12)", c: "#2F7D5A", label: "Published" },
  unpublished: { bg: "rgba(183,121,31,0.14)", c: "#B7791F", label: "Unpublished" },
  draft: { bg: "rgba(100,116,139,0.14)", c: "#475569", label: "Draft" },
  archived: { bg: "rgba(156,30,34,0.12)", c: "#9C1E22", label: "Archived" },
};

const StatusChip = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return (
    <span data-testid={`franchise-status-${status}`} className="text-[10px] font-bold px-2 py-0.5 rounded"
      style={{ backgroundColor: s.bg, color: s.c }}>{s.label}</span>
  );
};

export default function FranchiseAdmin() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [territories, setTerritories] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", subdomain: "", branch_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t, list] = await Promise.all([
        api.get("/franchise-admin/meta"),
        api.get("/franchise-admin/territories"),
        api.get("/franchise-admin/franchises"),
      ]);
      setMeta(m.data);
      setTerritories(t.data.territories || []);
      setCounts(list.data.counts || {});
    } catch (e) { toast.error(errMsg(e, "Could not load franchises.")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    if (!form.branch_id) { toast.error("Please link a branch (enforcement key)."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/franchise-admin/franchises", form);
      toast.success("Sub-site created (draft).");
      setCreateOpen(false);
      setForm({ name: "", slug: "", subdomain: "", branch_id: "" });
      navigate(`/franchise-admin/${data.franchise.id}`);
    } catch (e) { toast.error(errMsg(e, "Create failed.")); }
    finally { setCreating(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  const canCreate = meta?.capabilities?.can_create;
  const visibleTerritories = territories
    .map((t) => ({ ...t, franchises: (t.franchises || []).filter((f) => filter === "all" || f.status === filter) }))
    .filter((t) => t.franchises.length > 0 || filter === "all");

  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="franchise-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Building2 size={22} style={{ color: NAVY }} />
            <h1 className="text-2xl font-display font-bold text-[#1C4358]">Franchise / Sub-Site Manager</h1>
          </div>
          <p className="text-[14px] text-[#4B5563] mt-1">
            Manage territories and local sub-sites. Fields are labeled <ControlBadge kind="global" className="align-middle" />{" "}
            <ControlBadge kind="local" className="align-middle" /> <ControlBadge kind="hybrid" className="align-middle" /> per the field matrix.
          </p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <HelpButton route="/franchise-admin" testid="franchise-help-button" />
            <Button data-testid="legacy-review-btn" variant="outline"
              onClick={() => navigate("/franchise-admin/legacy")}
              className="gap-1.5 border-[#D6E0E7] text-[#1C4358]">
              <Archive size={16} /> Legacy / Unassigned
            </Button>
            <Button data-testid="create-franchise-btn" onClick={() => setCreateOpen(true)}
              className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
              <Plus size={16} /> New sub-site
            </Button>
          </div>
        )}
      </div>

      {/* Global-locked reminder */}
      <Card className="mt-4 p-4 border-[#E6EDF2] bg-[rgba(28,67,88,0.03)]">
        <div className="flex items-center gap-2 text-[13px] font-bold text-[#1C4358]">
          <Lock size={14} /> Controlled globally (Super Admin) <ControlBadge kind="global" />
        </div>
        <p className="text-[12px] text-[#4B5563] mt-1">
          Brand name, theme, service-lane templates, legal copy and email templates are inherited from the brand and are read-only here.
        </p>
      </Card>

      {/* Status filter */}
      <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="franchise-status-filter">
        {["all", "published", "unpublished", "draft", "archived"].map((s) => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
            className={`text-[13px] px-3 h-8 rounded-full border transition-colors ${filter === s
              ? "bg-[#1C4358] text-white border-[#1C4358]" : "bg-white text-[#4B5563] border-[#D6E0E7] hover:bg-[#1C4358]/5"}`}>
            {s === "all" ? "All" : (STATUS_STYLE[s]?.label || s)}
            {s !== "all" && counts[s] != null ? ` (${counts[s]})` : ""}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-6">
        {visibleTerritories.length === 0 && (
          <div className="text-center py-16 text-[#4B5563]" data-testid="franchise-empty">
            <MapPin size={28} className="mx-auto text-[#9BB9CE]" />
            <p className="mt-3">No sub-sites match this filter.</p>
          </div>
        )}
        {visibleTerritories.map((t) => (
          <div key={t.id} data-testid={`territory-group-${t.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Globe size={15} className="text-[#1C4358]" />
              <h2 className="text-[15px] font-display font-semibold text-[#1C4358]">{t.name}</h2>
              <span className="text-[12px] text-[#7A8A99]">{[t.state_name, t.country_name].filter(Boolean).join(" · ")}</span>
            </div>
            <div className="space-y-3">
              {t.franchises.map((f) => (
                <Card key={f.id} data-testid={`franchise-row-${f.id}`} className="p-4 border-[#E6EDF2] flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-[#1C4358] text-[16px] truncate">{f.name}</span>
                      <StatusChip status={f.status} />
                    </div>
                    <div className="text-[13px] text-[#4B5563] mt-0.5 flex items-center gap-3 flex-wrap">
                      {f.local_path && <span className="font-mono">{f.local_path}</span>}
                      {f.subdomain && <span className="text-[#7A8A99]">/?franchise={f.subdomain}</span>}
                      {f.local_phone && <span>{f.local_phone}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {f.subdomain && (
                      <a href={`/?franchise=${f.subdomain}`} target="_blank" rel="noreferrer" title="Preview"
                        data-testid={`franchise-preview-${f.id}`}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-[#1C4358] hover:bg-[#1C4358]/8"><ExternalLink size={16} /></a>
                    )}
                    <Button variant="outline" size="sm" data-testid={`franchise-edit-${f.id}`}
                      onClick={() => navigate(`/franchise-admin/${f.id}`)} className="gap-1 border-[#D6E0E7] text-[#1C4358]">
                      <Pencil size={14} /> Manage
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-franchise-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>New local sub-site</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="new-franchise-name" value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Toronto East Franchise" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Slug (optional)</Label>
                <Input data-testid="new-franchise-slug" value={form.slug}
                  onChange={(e) => setForm((s) => ({ ...s, slug: e.target.value }))} placeholder="auto from name" />
              </div>
              <div className="space-y-1.5">
                <Label>Subdomain (optional)</Label>
                <Input data-testid="new-franchise-subdomain" value={form.subdomain}
                  onChange={(e) => setForm((s) => ({ ...s, subdomain: e.target.value }))} placeholder="auto from slug" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Linked branch <span className="text-[#9C1E22]">*</span> <span className="text-[11px] text-[#7A8A99]">(enforcement key)</span></Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm((s) => ({ ...s, branch_id: v }))}>
                <SelectTrigger data-testid="new-franchise-branch"><SelectValue placeholder="Select a branch" /></SelectTrigger>
                <SelectContent>
                  {(meta?.branches || []).map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}{b.country ? ` · ${b.country}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid="new-franchise-submit" onClick={create} disabled={creating}
              className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5">
              {creating && <Loader2 size={14} className="animate-spin" />} Create draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
